import React, { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/store-hooks';
import { useSafeEffect } from '../../../app/hooks/use-safe-effect';
import {
    addPinnedFlight,
    changeFlightDetailsTabIndex,
    fastForwardFlight,
    followFlight,
    removePinnedFlight,
    toggleFlightIndividual,
    toggleLeftFlyout,
    WorkspaceState
} from '../../../app/state/slices/workspace-slice';
import { FlightContextMenuDto } from '../../../flights/interop/flightDto';
import useMapInteraction from '../../../hooks/use-map-interaction';
import {
    AirplaneOutline,
    AirplaneSolid,
    AirportTowerOutline,
    AttachImageOutline,
    BorderColorOutline,
    ChartsLineOutline,
    CheckmarkOutline,
    DeltaOutline,
    DoubleChevronRightOutline,
    DownloadOutline,
    EditOutline,
    ExternalLinkOutline,
    InfoCircleOutline,
    PinOutline,
    PinSolid,
    StopCircleOutline,
    UploadSolid,
    WindyOutline
} from '../../../icons/icons';
import { MessageType } from '../../../services/actor-messages';
import { EliService } from '../../../services/external-link-interface/EliService';
import { EliCustomButton } from '../../../types/settings';
import { Menu, MenuItem, NavigatingMenuItem } from '../../common/menu/DropdownMenu';

import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router';
import { ContextMenuFlightThresholdsList } from '../../../app/components/monitor-status/ContextMenuFlightThresholdsList';
import { EventBus } from '../../../app/contexts/event-bus';
import WorkspaceItemContext from '../../../app/contexts/workspace/workspace-item-context';
import { FlightDataEntryRequestBase, FlightEntryWindowType } from '../../../app/features/flight-data-entry/types';
import { OpenNotesCommand } from '../../../app/features/home/flyouts/Notes';
import { useFocusedWorkspaceSelector, useWorkspace } from '../../../app/features/home/hooks/use-view';
import { ScreenshotCommand, ScreenshotCommandType } from '../../../app/features/home/layers/MapScreenshot';
import { useFlightWorker } from '../../../app/hooks/use-flight-worker';
import { RoutePath, ROUTES } from '../../../app/router/routes';
import { patchFocusedWorkspace } from '../../../app/state/store';
import { FlightStatus } from '../../../common/enums';
import useIntegration, { FPMGoToFlight, FPMRequestRoute, FPMRequestRouteVariants } from '../../../hooks/use-integration';
import { NoteCategory } from '../../../types/notes';

export function FlightContextMenuContent(props: { id: number }) {
    const context = { id: props.id };
    const navigate = useNavigate();
    const workspaceContextId = React.useContext(WorkspaceItemContext).id;
    const dispatch = useAppDispatch();
    const fastForward = useWorkspace((x) => x.flights.fastForward);
    const [flightContextMenu, setFlightContextMenu] = React.useState<FlightContextMenuDto | null>(null);
    const individual = useAppSelector((x) => x.workspace.individual);
    const rights = useAppSelector((x) => x.user.rights);
    const isPlayback = useAppSelector((x) => x.global.playback.active);
    const interactionService = useMapInteraction();
    const isPinned = useAppSelector((x) => x.workspace.flights.pinned.some((x) => x.id === context.id));
    const isFollowed = useFocusedWorkspaceSelector((x) => x.flights.followed === context.id);
    const imageServerUrl = useAppSelector((x) => x.settings.mapImageServerUrl);
    const location = useLocation();
    const integrationInfo = useIntegration();
    const worker = useFlightWorker();
    const isBeingFastForwarded = fastForward.active && fastForward.flightId === context.id;
    const activeFlight = flightContextMenu && flightContextMenu?.status < FlightStatus.Planned;

    React.useEffect(() => {
        if (context.id === undefined) {
            setFlightContextMenu(null);
        } else if (context.id === -1) {
            // Tutorial
            setFlightContextMenu({
                id: -1,
                acid: 'FE123',
                status: FlightStatus.Active,
                origin: 'KLAX',
                destination: 'KJFK',
                alternate1: 'KBDL',
                alternate2: 'KBOX',
                otherAlternates: [],
                etopsAlternates: [],
                regId: 'N123AB',
                oetd: null,
                notam: null,
                runway: '3L'
            } satisfies FlightContextMenuDto);
        } else {
            worker.actor.sendAsync({ type: MessageType.getFlightContextMenuModel, data: context.id }).then((x) => setFlightContextMenu(x));
        }
    }, [worker, context.id]);

    const navigateIfNeeded = useCallback(
        (navigateTo: RoutePath = ROUTES.HOME) => {
            if (location.pathname !== navigateTo) {
                navigate(navigateTo);
                return true;
            }
            return false;
        },
        [navigate, location]
    );

    const toggleIndividual = React.useCallback(
        (type: keyof WorkspaceState['individual']) => {
            dispatch(toggleFlightIndividual({ type, id: context!.id! }));
        },
        [context.id, dispatch]
    );

    const handleFlightDetails = React.useCallback(() => {
        EventBus.defer('selectedMap', 'select-flight', { flightId: context!.id!, options: { source: 'context-menu' } });
        navigateIfNeeded();
        interactionService.dismissContextMenu();
    }, [context.id]);

    const handleEmergencyDiversion = React.useCallback(() => {
        dispatch(toggleLeftFlyout({ type: 'diversion', flightId: context!.id }));
        navigateIfNeeded();
        interactionService.dismissContextMenu();
    }, [context.id]);

    const handleAnalyzeRoutes = React.useCallback(() => {
        if (location.pathname !== ROUTES.HOME) {
            EventBus.defer('home', 'route-analyzer-load-flight', context!.id!);
            navigate(ROUTES.HOME);
        } else {
            EventBus.fire('route-analyzer-load-flight', context!.id!);
        }
        interactionService.dismissContextMenu();
    }, [context.id]);

    const handleNotes = React.useCallback(() => {
        if (location.pathname !== ROUTES.HOME) {
            if (flightContextMenu) {
                EventBus.fire('show-notes-window', {
                    flightId: flightContextMenu.id,
                    itemId: flightContextMenu.acid,
                    category: NoteCategory.aircraft
                } satisfies OpenNotesCommand);
            }
        } else {
            dispatch(changeFlightDetailsTabIndex(3));
            EventBus.defer('selectedMap', 'select-flight', { flightId: context!.id!, options: { source: 'side-effect' } });
        }
        interactionService.dismissContextMenu();
    }, [context.id, flightContextMenu]);

    const handleScratchPad = React.useCallback(() => {
        EventBus.fire('show-scratchpad-window', { flightId: context.id });
        interactionService.dismissContextMenu();
    }, [context.id, interactionService]);

    const getCustomFileName = React.useCallback(() => {
        if (flightContextMenu) {
            const acid = flightContextMenu.acid ?? '';
            const origin = flightContextMenu.origin ?? '';
            const timestamp = dayjs().utc().format('YYYYMMDDHHmmss');
            return `${acid}_${origin}_${timestamp}`;
        }
        return undefined;
    }, [flightContextMenu]);

    const handleSaveImage = React.useCallback(
        (type: Exclude<ScreenshotCommandType, ScreenshotCommandType.slideShowAutomatic>) => {
            const screenshotCommand = {
                type,
                workspaceIndex: workspaceContextId,
                customFilename: getCustomFileName()
            } satisfies ScreenshotCommand;

            interactionService.dismissContextMenu();
            if (location.pathname !== ROUTES.HOME) {
                EventBus.defer('selectedMap', 'take-map-screenshot', screenshotCommand);
                navigate(ROUTES.HOME);
            } else {
                EventBus.fire('take-map-screenshot', screenshotCommand);
            }
        },
        [getCustomFileName, workspaceContextId, interactionService, location.pathname, dispatch, navigate]
    );

    const getFlightDataEntryRequest = React.useCallback((): FlightDataEntryRequestBase | null => {
        if (!flightContextMenu || !flightContextMenu.acid || !flightContextMenu.origin || !flightContextMenu.oetd) {
            return null;
        }
        return {
            acid: flightContextMenu.acid,
            origin: flightContextMenu.origin,
            departureDateTime: flightContextMenu.oetd,
            regId: flightContextMenu.regId
        };
    }, [flightContextMenu]);

    const handleRequestAcarsPositionUpdate = React.useCallback(() => {
        if (!flightContextMenu) {
            return;
        }
        const flightDataEntryRequest = getFlightDataEntryRequest();
        if (!flightDataEntryRequest) {
            return;
        }
        EventBus.fire('show-flight-entry-window', { type: FlightEntryWindowType.AcarsPosition, flightInfo: flightDataEntryRequest });
        interactionService.dismissContextMenu();
    }, [flightContextMenu, interactionService, getFlightDataEntryRequest]);

    const handleSetAcarsPositionUpdatePeriod = React.useCallback(() => {
        if (!flightContextMenu) {
            return;
        }
        const flightDataEntryRequest = getFlightDataEntryRequest();
        if (!flightDataEntryRequest) {
            return;
        }
        EventBus.fire('show-flight-entry-window', { type: FlightEntryWindowType.AcarsSetPeriod, flightInfo: flightDataEntryRequest });
        interactionService.dismissContextMenu();
    }, [flightContextMenu, interactionService, getFlightDataEntryRequest]);

    const handleManualFlightDataEntry = React.useCallback(
        (type: FlightEntryWindowType) => {
            if (!flightContextMenu) {
                return;
            }
            const flightDataEntryRequest = getFlightDataEntryRequest();
            if (!flightDataEntryRequest) {
                return;
            }
            EventBus.fire('show-flight-entry-window', { type, flightInfo: flightDataEntryRequest });
            interactionService.dismissContextMenu();
        },
        [flightContextMenu, interactionService, getFlightDataEntryRequest]
    );

    const hasDataForFlightDataEntry = React.useCallback(() => {
        if (!flightContextMenu || !flightContextMenu.acid || !flightContextMenu.origin || !flightContextMenu.oetd) {
            return false;
        }
        return true;
    }, [flightContextMenu]);

    const handleShowNotam = React.useCallback(() => {
        if (!flightContextMenu?.notam) {
            return;
        }
        if (!/^\d{1,2}-\d{1,4}-\d{2}$/.test(flightContextMenu.notam)) {
            return;
        }
        EventBus.fire('show-notam-window', { notam: flightContextMenu.notam });
        interactionService.dismissContextMenu();
    }, [flightContextMenu, interactionService]);

    const handlePin = () => {
        if (isPinned) {
            dispatch(removePinnedFlight({ workspaceId: workspaceContextId, flightId: context!.id }));
        } else {
            dispatch(addPinnedFlight({ workspaceId: workspaceContextId, flightId: context!.id }));
        }
    };

    const handleFollow = () => {
        if (isFollowed) {
            dispatch(followFlight(null));
        } else {
            dispatch(followFlight(context!.id));
        }
    };

    const handleRunwayWinds = React.useCallback(
        (airportId: string, label: string, flightRunway?: string | null) => {
            EventBus.fire('show-runway-winds', { airportId, label, flightRunway });
            interactionService.dismissContextMenu();
        },
        [interactionService]
    );

    const handleAirportSummary = React.useCallback(
        (airportId: string) => {
            dispatch(toggleLeftFlyout({ type: 'airport', airportId }));
            navigateIfNeeded();
            interactionService.dismissContextMenu();
        },
        [dispatch, navigateIfNeeded, interactionService]
    );

    const hasAnyAirportSummaryItem =
        flightContextMenu?.origin ||
        flightContextMenu?.destination ||
        flightContextMenu?.alternate1 ||
        flightContextMenu?.alternate2 ||
        !!flightContextMenu?.otherAlternates.length ||
        !!flightContextMenu?.etopsAlternates.length;

    const goToFlightDisabledReason = integrationInfo.getDisabledReason('canGoToFlightInFPM');
    const requestRoutesDisabledReason =
        integrationInfo.getDisabledReason('canRequestRouteFromFPM') ??
        (!flightContextMenu
            ? 'Loading flight details…'
            : !flightContextMenu.acid || !flightContextMenu.origin || !flightContextMenu.destination
              ? 'Flight has no ACID, origin or destination'
              : null);
    const getFlightInfoDisabledReason = 'This feature is not yet implemented.';

    return (
        <>
            <NavigatingMenuItem label={'Flight details'} icon={InfoCircleOutline} onClick={handleFlightDetails} navigateTo={ROUTES.HOME} />
            <Menu label="Breached thresholds">
                <MenuItem>
                    <ContextMenuFlightThresholdsList flightId={context.id} />
                </MenuItem>
            </Menu>
            {hasAnyAirportSummaryItem && (
                <Menu label="Airport summary" icon={AirportTowerOutline}>
                    <AirportSummaryMenuItem airportId={flightContextMenu?.origin} type="origin" onClick={handleAirportSummary} />
                    <AirportSummaryMenuItem airportId={flightContextMenu?.destination} type="destination" onClick={handleAirportSummary} />
                    <AirportSummaryMenuItem
                        airportId={flightContextMenu?.alternate1}
                        type="destination alternate 1"
                        onClick={handleAirportSummary}
                    />
                    <AirportSummaryMenuItem
                        airportId={flightContextMenu?.alternate2}
                        type="destination alternate 2"
                        onClick={handleAirportSummary}
                    />
                    {flightContextMenu?.otherAlternates.map((x) => (
                        <AirportSummaryMenuItem key={x} airportId={x} type="destination alternate other" onClick={handleAirportSummary} />
                    ))}
                    {flightContextMenu?.etopsAlternates.map((x) => (
                        <AirportSummaryMenuItem key={x} airportId={x} type="ETOPS/EDTO alternate" onClick={handleAirportSummary} />
                    ))}
                </Menu>
            )}
            <Menu label="Show" closeOnClick={false} className="border-top">
                {activeFlight && (
                    <>
                        <MenuItem
                            icon={individual.tags.includes(context.id) ? CheckmarkOutline : undefined}
                            onClick={() => toggleIndividual('tags')}
                            label="Tag"
                        />
                        <MenuItem
                            icon={individual.history.includes(context.id) ? CheckmarkOutline : undefined}
                            onClick={() => toggleIndividual('history')}
                            label="History"
                        />
                        <MenuItem
                            icon={individual.vector.includes(context.id) ? CheckmarkOutline : undefined}
                            onClick={() => toggleIndividual('vector')}
                            label="Vector"
                        />
                        <MenuItem
                            icon={individual.rangeRings.includes(context.id) ? CheckmarkOutline : undefined}
                            onClick={() => toggleIndividual('rangeRings')}
                            label="Range ring"
                        />
                    </>
                )}
                <MenuItem
                    icon={individual.flightPath.includes(context.id) ? CheckmarkOutline : undefined}
                    onClick={() => toggleIndividual('flightPath')}
                    label="Flight plan"
                />
                {activeFlight && (
                    <MenuItem
                        icon={individual.routeFlown.includes(context.id) ? CheckmarkOutline : undefined}
                        onClick={() => toggleIndividual('routeFlown')}
                        label="Route flown"
                    />
                )}
            </Menu>
            {flightContextMenu?.destination && (
                <MenuItem
                    id="flight-context-menu-destination-winds"
                    className="border-top"
                    icon={WindyOutline}
                    label={`Destination winds — ${flightContextMenu.destination.trim()}`}
                    onClick={() =>
                        handleRunwayWinds(
                            flightContextMenu.destination!.trim(),
                            flightContextMenu.destination!.trim(),
                            flightContextMenu.runway
                        )
                    }
                />
            )}
            <MenuItem label="Notes" icon={BorderColorOutline} onClick={handleNotes} className="border-top" />
            <MenuItem label="Scratchpad" icon={EditOutline} onClick={handleScratchPad} />
            <MenuItem label={isPinned ? 'Unpin' : 'Pin'} icon={isPinned ? PinSolid : PinOutline} onClick={handlePin} />
            {activeFlight && (
                <MenuItem
                    label={isFollowed ? 'Stop following' : 'Follow'}
                    icon={isFollowed ? AirplaneSolid : AirplaneOutline}
                    onClick={handleFollow}
                />
            )}
            {activeFlight && (
                <NavigatingMenuItem
                    className="border-top"
                    label="Emergency diversion"
                    icon={DeltaOutline}
                    navigateTo={ROUTES.HOME}
                    onClick={handleEmergencyDiversion}
                />
            )}
            <NavigatingMenuItem
                disabled={isPlayback}
                label="Analyze routes"
                icon={ChartsLineOutline}
                navigateTo={ROUTES.HOME}
                onClick={handleAnalyzeRoutes}
            />
            <Menu label="Save map image" icon={AttachImageOutline} onClick={() => handleSaveImage(ScreenshotCommandType.save)}>
                <NavigatingMenuItem
                    label="Save"
                    icon={DownloadOutline}
                    navigateTo={ROUTES.HOME}
                    onClick={() => handleSaveImage(ScreenshotCommandType.save)}
                />
                <NavigatingMenuItem
                    label="Save as..."
                    disabled={!('showSaveFilePicker' in window)}
                    icon={EditOutline}
                    navigateTo={ROUTES.HOME}
                    onClick={() => handleSaveImage(ScreenshotCommandType.saveAs)}
                />
                <NavigatingMenuItem
                    label="Upload to server"
                    icon={UploadSolid}
                    navigateTo={ROUTES.HOME}
                    disabled={!(imageServerUrl && imageServerUrl.trim().length > 0)}
                    onClick={() => handleSaveImage(ScreenshotCommandType.serverUpload)}
                />
            </Menu>
            <MenuItem
                id="flight-context-menu-fast-forward"
                icon={isBeingFastForwarded ? StopCircleOutline : DoubleChevronRightOutline}
                label={isBeingFastForwarded ? 'Stop fast forward' : 'Fast forward'}
                onClick={() => {
                    // TODO spinner in progress?
                    if (isBeingFastForwarded) {
                        dispatch(patchFocusedWorkspace((x) => (x.flights.fastForward = { active: false })));
                    } else {
                        dispatch(fastForwardFlight({ flightId: flightContextMenu!.id, workspaceId: workspaceContextId })).then(() => {
                            EventBus.defer('selectedMap', 'select-flight', {
                                flightId: flightContextMenu!.id,
                                options: { source: 'context-menu' }
                            });
                        });
                    }
                }}></MenuItem>

            {!isPlayback && (
                <>
                    {rights.manualFlightDataEntry && (
                        <Menu label="Manual entry" className="border-top">
                            <MenuItem
                                icon={ExternalLinkOutline}
                                label="OOOI times"
                                disabled={!hasDataForFlightDataEntry()}
                                onClick={() => handleManualFlightDataEntry(FlightEntryWindowType.ManualOOOITimes)}
                            />
                            <MenuItem
                                icon={ExternalLinkOutline}
                                label="Diversion recovery priority"
                                disabled={!hasDataForFlightDataEntry()}
                                onClick={() => handleManualFlightDataEntry(FlightEntryWindowType.ManualDiversionRecoveryPriority)}
                            />
                            <MenuItem
                                icon={ExternalLinkOutline}
                                label="Gate info"
                                disabled={!hasDataForFlightDataEntry()}
                                onClick={() => handleManualFlightDataEntry(FlightEntryWindowType.ManualGateInfo)}
                            />
                        </Menu>
                    )}
                    {(rights.acarsUpdateNow || rights.acarsUpdateInterval) && (
                        <Menu label="ACARS" className="border-top">
                            {rights.acarsUpdateNow && (
                                <MenuItem
                                    icon={ExternalLinkOutline}
                                    label="Request position update"
                                    disabled={!hasDataForFlightDataEntry()}
                                    onClick={handleRequestAcarsPositionUpdate}
                                />
                            )}
                            {rights.acarsUpdateInterval && (
                                <MenuItem
                                    icon={ExternalLinkOutline}
                                    label="Set position update period"
                                    disabled={!hasDataForFlightDataEntry()}
                                    onClick={handleSetAcarsPositionUpdatePeriod}
                                />
                            )}
                        </Menu>
                    )}
                    {flightContextMenu?.notam && (
                        <MenuItem
                            label="View full NOTAM text"
                            className={rights.acarsUpdateNow || rights.acarsUpdateInterval ? undefined : 'border-top'}
                            onClick={handleShowNotam}
                        />
                    )}

                    {integrationInfo.enabled && (
                        <>
                            <MenuItem
                                className="border-top"
                                label={`Go to flight in ${integrationInfo.typeText}`}
                                disabled={!!goToFlightDisabledReason}
                                hint={goToFlightDisabledReason}
                                onClick={() => {
                                    FPMGoToFlight(context.id, flightContextMenu?.acid ?? null);
                                    interactionService.dismissContextMenu();
                                }}
                            />
                            <>
                                <MenuItem
                                    label="Show all flight plan routes"
                                    disabled={!!requestRoutesDisabledReason}
                                    hint={requestRoutesDisabledReason}
                                    onClick={() => {
                                        if (!flightContextMenu) {
                                            return;
                                        }
                                        FPMRequestRoute(flightContextMenu);
                                        interactionService.dismissContextMenu();
                                    }}
                                />
                                <MenuItem
                                    label="Show flight plan route variants"
                                    disabled={!!requestRoutesDisabledReason}
                                    hint={requestRoutesDisabledReason}
                                    onClick={() => {
                                        if (!flightContextMenu) {
                                            return;
                                        }
                                        FPMRequestRouteVariants(flightContextMenu);
                                        interactionService.dismissContextMenu();
                                    }}
                                />
                            </>
                            {integrationInfo.isDesktop && (
                                <>
                                    <MenuItem
                                        label={`Get properties from ${integrationInfo.typeText}`}
                                        // disabled={!integrationInfo.isActionEnabled('canGetPropertiesFromFPM')}
                                        disabled={true}
                                        hint={getFlightInfoDisabledReason}
                                    />
                                </>
                            )}
                        </>
                    )}
                </>
            )}
            {flightContextMenu && <FlightEli flight={flightContextMenu} />}
        </>
    );
}

export function FlightEli({ flight }: { flight: FlightContextMenuDto }) {
    const eli = useAppSelector((x) => x.settings.eliAirplaneButtons);
    const [filtered, setFiltered] = React.useState<EliCustomButton[]>([]);
    const worker = useFlightWorker();
    const handleOnClick = (eli: EliCustomButton) => {
        EliService.handleAircraft(eli, flight);
    };

    useSafeEffect(() => {
        const handler = async () => {
            const result: EliCustomButton[] = [];
            for (const item of eli) {
                if (!item.filter) {
                    result.push(item);
                } else if (
                    await worker.actor.sendAsync({
                        type: MessageType.validatePattern,
                        data: {
                            pattern: item.filter,
                            text: flight.acid ?? flight.regId ?? '',
                            isAirport: false
                        }
                    })
                ) {
                    result.push(item);
                }
            }
            setFiltered(result);
        };
        handler();
    }, [worker, eli, flight]);

    if (filtered.length >= 3) {
        return (
            <Menu label="External Link Interface" className="border-top">
                {filtered.map((x, i) => (
                    <MenuItem key={i} label={x.label} onClick={() => handleOnClick(x)}></MenuItem>
                ))}
            </Menu>
        );
    }
    return (
        <>
            <div className="border-top"></div>
            {filtered.map((x, i) => (
                <MenuItem key={i} label={x.label} onClick={() => handleOnClick(x)}></MenuItem>
            ))}
        </>
    );
}

function AirportSummaryMenuItem(props: { airportId: string | undefined | null; type: string; onClick: (airportId: string) => void }) {
    if (!props.airportId) {
        return null;
    }

    return (
        <NavigatingMenuItem
            label={`${props.airportId} (${props.type})`}
            onClick={() => props.onClick(props.airportId!)}
            navigateTo={ROUTES.HOME}
        />
    );
}
